import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/layout/Header";
import { Sidebar } from "@/components/layout/Sidebar";
import { Helmet } from "react-helmet-async";
import { useWallet } from "@/contexts/WalletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  UserCheck, Shield, TrendingUp, Lock, Copy, DollarSign,
  Users, Zap, CheckCircle, AlertTriangle, Wallet
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Trader {
  id: string;
  wallet: string;
  displayName: string;
  winRate: number;
  totalProfit: number;
  subscribers: number;
  monthlyFee: number;
  isProtected: boolean;
  verified: boolean;
}

// MOCK_TRADERS removed in favor of real data


export default function CopyTradingSubscriptions() {
  const { connected, publicKey, connect } = useWallet();
  const [activeTab, setActiveTab] = useState("discover");
  const [becomeTrader, setBecomeTrader] = useState(false);

  const [traderSettings, setTraderSettings] = useState({
    monthlyFee: 0.5,
    enableProtection: true,
    allowCopying: true,
  });

  const { data: realTraders, isLoading } = useQuery({
    queryKey: ["copy-trading-available"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leaderboard_entries")
        .select("*")
        .order("total_profit", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data;
    }
  });

  return (
    <>
      <Helmet>
        <title>Copy Trading Subscriptions - TheMoonRun</title>
        <meta name="description" content="Subscribe to top traders and copy their trades automatically. Earn by letting others copy you." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex">
          <Sidebar />
          <main className="flex-1 min-w-0 p-4 lg:p-6">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
                    <UserCheck className="w-7 h-7 text-green-500" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-display font-bold text-foreground">Copy Trading</h1>
                    <p className="text-muted-foreground">Subscribe to traders or earn from your trades</p>
                  </div>
                </div>
              </div>

              {/* Protection Banner */}
              <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30 mb-6 flex items-start gap-3">
                <Shield className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Protected Trading Wallets</h3>
                  <p className="text-sm text-muted-foreground">
                    Our system blocks external copy trading services. Only subscribers through TheMoonRun can copy trades,
                    ensuring traders get paid fairly and the ecosystem remains secure.
                  </p>
                </div>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="discover">Discover Traders</TabsTrigger>
                  <TabsTrigger value="subscriptions">My Subscriptions</TabsTrigger>
                  <TabsTrigger value="become">Become a Trader</TabsTrigger>
                </TabsList>

                <TabsContent value="discover" className="space-y-4">
                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: "Active Traders", value: "234", icon: Users },
                      { label: "Total Subscribers", value: "12.4K", icon: UserCheck },
                      { label: "Avg Win Rate", value: "67.8%", icon: TrendingUp },
                      { label: "Total Volume", value: "1.2M SOL", icon: DollarSign },
                    ].map((stat, i) => (
                      <div key={i} className="p-4 rounded-xl bg-card border border-border">
                        <stat.icon className="w-5 h-5 text-primary mb-2" />
                        <p className="text-xl font-bold text-foreground">{stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* Traders Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {isLoading ? (
                      Array(3).fill(0).map((_, i) => (
                        <div key={i} className="p-5 rounded-xl bg-card border border-border h-48 animate-pulse">
                          <div className="h-12 w-12 bg-secondary rounded-xl mb-4" />
                          <div className="h-4 w-32 bg-secondary rounded mb-2" />
                        </div>
                      ))
                    ) : realTraders && realTraders.length > 0 ? (
                      realTraders.map((trader) => (
                        <div
                          key={trader.id}
                          className="p-5 rounded-xl bg-card border border-border hover:border-primary/50 transition-all text-left"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                                <span className="text-lg font-bold text-primary">
                                  {(trader.display_name || "T").charAt(0)}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-foreground">{trader.display_name || "Trader"}</h3>
                                  {trader.win_rate > 70 && (
                                    <CheckCircle className="w-4 h-4 text-primary" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground font-mono">
                                  {trader.wallet_address.slice(0, 4)}...{trader.wallet_address.slice(-4)}
                                </p>
                              </div>
                            </div>
                            {/* Protection Badge (Simulated for all valid traders for now) */}
                            <Badge variant="outline" className="text-green-500 border-green-500/50">
                              <Lock className="w-3 h-3 mr-1" />
                              Protected
                            </Badge>
                          </div>

                          <div className="grid grid-cols-3 gap-2 mb-4">
                            <div className="p-2 rounded-lg bg-secondary/50 text-center">
                              <p className="text-xs text-muted-foreground">Win Rate</p>
                              <p className={cn(
                                "font-mono font-semibold",
                                (trader.win_rate || 0) >= 70 ? "text-success" : "text-foreground"
                              )}>
                                {(trader.win_rate || 0).toFixed(1)}%
                              </p>
                            </div>
                            <div className="p-2 rounded-lg bg-secondary/50 text-center">
                              <p className="text-xs text-muted-foreground">Profit</p>
                              <p className="font-mono font-semibold text-success">
                                +{(trader.total_profit || 0).toFixed(2)}
                              </p>
                            </div>
                            <div className="p-2 rounded-lg bg-secondary/50 text-center">
                              <p className="text-xs text-muted-foreground">Trades</p>
                              <p className="font-mono font-semibold text-foreground">
                                {trader.total_trades || 0}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <p className="text-xs text-muted-foreground">Monthly Fee</p>
                              <p className="font-mono font-bold text-primary">0.5 SOL</p>
                            </div>
                            <Button variant="default" size="sm" className="gap-2">
                              <Copy className="w-4 h-4" />
                              Subscribe
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-span-3 text-center py-10 text-muted-foreground">
                        No active copy-traders found. Be the first!
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="subscriptions" className="space-y-4">
                  {!connected ? (
                    <div className="text-center py-12">
                      <Wallet className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground mb-4">Connect wallet to view subscriptions</p>
                      <Button variant="hero" onClick={connect}>Connect Wallet</Button>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <UserCheck className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground">No active subscriptions</p>
                      <p className="text-sm text-muted-foreground mb-4">Subscribe to traders to start copying</p>
                      <Button variant="outline" onClick={() => setActiveTab("discover")}>
                        Discover Traders
                      </Button>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="become" className="space-y-4">
                  {!connected ? (
                    <div className="text-center py-12">
                      <Wallet className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground mb-4">Connect wallet to become a trader</p>
                      <Button variant="hero" onClick={connect}>Connect Wallet</Button>
                    </div>
                  ) : (
                    <div className="max-w-xl mx-auto">
                      <div className="p-6 rounded-xl bg-card border border-border space-y-6">
                        <div className="text-center mb-6">
                          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                            <DollarSign className="w-8 h-8 text-primary" />
                          </div>
                          <h2 className="text-xl font-bold text-foreground mb-2">Earn From Your Trades</h2>
                          <p className="text-muted-foreground">
                            Let others copy your trades and earn subscription fees
                          </p>
                        </div>

                        {/* Benefits */}
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { icon: Shield, label: "Protected Wallet" },
                            { icon: DollarSign, label: "Monthly Revenue" },
                            { icon: Lock, label: "Block Freeloaders" },
                            { icon: Zap, label: "Instant Payouts" },
                          ].map((benefit, i) => (
                            <div key={i} className="p-3 rounded-lg bg-secondary/50 flex items-center gap-2">
                              <benefit.icon className="w-4 h-4 text-primary" />
                              <span className="text-sm text-foreground">{benefit.label}</span>
                            </div>
                          ))}
                        </div>

                        {/* Settings */}
                        <div className="space-y-4">
                          <div>
                            <label className="text-sm font-medium text-foreground mb-2 block">
                              Monthly Subscription Fee (SOL)
                            </label>
                            <Input
                              type="number"
                              step="0.1"
                              min="0.1"
                              value={traderSettings.monthlyFee}
                              onChange={(e) => setTraderSettings(s => ({ ...s, monthlyFee: parseFloat(e.target.value) || 0.1 }))}
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Platform takes 10% fee from subscriptions
                            </p>
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-foreground">Enable Wallet Protection</p>
                              <p className="text-sm text-muted-foreground">Block external copy trading services</p>
                            </div>
                            <Switch
                              checked={traderSettings.enableProtection}
                              onCheckedChange={(checked) => setTraderSettings(s => ({ ...s, enableProtection: checked }))}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-foreground">Allow Copy Trading</p>
                              <p className="text-sm text-muted-foreground">Enable subscribers to copy your trades</p>
                            </div>
                            <Switch
                              checked={traderSettings.allowCopying}
                              onCheckedChange={(checked) => setTraderSettings(s => ({ ...s, allowCopying: checked }))}
                            />
                          </div>
                        </div>

                        <Button variant="hero" className="w-full">
                          <UserCheck className="w-4 h-4" />
                          Register as Copy Trader
                        </Button>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
